import type { QueryClient } from '@tanstack/react-query';

/**
 * Everything this client has cached belongs to whoever was signed in when it was fetched.
 *
 * `auth.me` is the only query that names the player, but it is nowhere near the only one keyed
 * to them: `profile.me`, `uploads.listImages`, `sayings.list`, `leaderboard.standing` and every
 * admin query answer "for the account holding this session" and carry no user id in their key.
 * So the moment the session becomes a *different* person — sign in, register, guest, Google,
 * guest upgrade, sign out — every one of those entries is an answer about somebody else, and a
 * client that only invalidated `auth.me` served the rest of them stale until a hard refresh.
 * That is the bug this exists to close.
 *
 * It is a RESET rather than a list of invalidations on purpose: a list is a thing somebody has
 * to remember to extend the next time a procedure is added, and the one time it is forgotten is
 * a player looking at a stranger's name.
 *
 * The two halves are the two kinds of cache entry. Nothing is observing an inactive query, so
 * it is dropped outright and the next mount fetches under the new identity. An active one is
 * on screen, so it is reset to its loading state and refetched — including `auth.me`, which is
 * mounted for the whole life of the app. Neither path consults `staleTime`, which is what makes
 * this independent of a stale window expiring.
 */
export async function onIdentityChanged(queryClient: QueryClient): Promise<void> {
  queryClient.removeQueries({ type: 'inactive' });
  await queryClient.resetQueries({ type: 'active' });
}
